import { getSTSToken } from '@/api/tool/common'
import COS from 'cos-js-sdk-v5'
import OSS from 'ali-oss'
import request from '@/utils/request'

// OSS/COS 全局实例（单例模式）
let ossInstance = null
let cosInstance = null
// 根路径配置（与后端一致）
const ROOT_PREFIX = 'oss-file/'

/**
 * 初始化阿里云OSS客户端（自动续期Token）
 */
async function initAliOssClient() {
  if (ossInstance) return ossInstance

  // 获取STS Token
  const stsRes = await getSTSToken()
  const stsToken = stsRes.data

  // 构建自动续期的OSS客户端
  ossInstance = new OSS({
    accessKeyId: stsToken.accessKeyId,
    accessKeySecret: stsToken.accessKeySecret,
    stsToken: stsToken.securityToken,
    bucket: stsToken.bucket,
    endpoint: stsToken.endpoint,
    region: stsToken.region,
    // 自定义超时时间
    timeout: 60000,
    // 自动刷新Token（核心：Token过期前重新获取）
    refreshSTSToken: async () => {
      const newStsRes = await getSTSToken()
      const newStsToken = newStsRes.data
      return {
        accessKeyId: newStsToken.accessKeyId,
        accessKeySecret: newStsToken.accessKeySecret,
        stsToken: newStsToken.securityToken
      }
    },
    // Token过期时间（提前30秒刷新）
    refreshSTSTokenInterval: 3570 * 1000 // 3600秒有效期，提前30秒刷新
  })
  return ossInstance
}

/**
 * 初始化腾讯云COS客户端（自动续期Token）
 */
function initTencentCosClient() {
  if (cosInstance) return cosInstance

  const baseUrl = process.env.VUE_APP_BASE_API
  cosInstance = new COS({
    getAuthorization: function (options, callback) {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', `${baseUrl}/common/oss`, true)
      xhr.onload = function (e) {
        try {
          const data = JSON.parse(e.target.responseText)
          const credentials = data.data
          if (!data || !credentials) {
            return console.error('COS凭证获取失败：', JSON.stringify(data))
          }
          callback({
            TmpSecretId: credentials.accessKeyId,
            TmpSecretKey: credentials.accessKeySecret,
            SecurityToken: credentials.securityToken,
            StartTime: credentials.startTime,
            ExpiredTime: credentials.expiration
          })
        } catch (e) {
          console.error('COS凭证解析失败：', e)
        }
      }
      xhr.send()
    }
  })
  return cosInstance
}

// 获取上传请求头
export function getHeaders() {
  return {
    'Content-Encoding': 'UTF-8',
    'x-oss-forbid-overwrite': 'true' // 禁止覆盖同名文件
  }
}

/**
 * 通用上传方法（支持阿里云/腾讯云）
 * @param filePath 自定义文件路径（不传则自动生成）
 * @param file 文件对象
 * @param prefix 自定义前缀（可选，用于覆盖默认根路径）
 */
export async function uploadObject(filePath, file, prefix = ROOT_PREFIX) {
  if (!file) throw new Error('上传文件不能为空')

  // 自动生成文件路径（如果未传）
  const finalFilePath = filePath || uploadOss(file.name, prefix.replace(ROOT_PREFIX, ''))

  // 获取STS Token判断厂商类型
  const stsToken = await getSTSToken()
  const type = stsToken.data.type

  if (type === 'aliyun') {
    return uploadOssObject(finalFilePath, file, stsToken)
  } else if (type === 'tencent') {
    return uploadCosObject(finalFilePath, file, stsToken)
  } else {
    throw new Error(`不支持的存储类型：${type}`)
  }
}

/**
 * 阿里云OSS上传
 */
async function uploadOssObject(filePath, file, stsToken) {
  const client = await initAliOssClient()
  const headers = getHeaders()

  // 分片上传（支持大文件）
  const result = await client.multipartUpload(filePath, file, { headers })
  if (result.res.status === 200) {
    return stsToken.data.staticUrl + filePath
  }
  throw new Error('阿里云OSS上传失败')
}

/**
 * 腾讯云COS上传
 */
async function uploadCosObject(filePath, file, token) {
  const cos = initTencentCosClient()
  return new Promise((resolve, reject) => {
    cos.uploadFile({
      Bucket: token.data.bucket,
      Region: token.data.region,
      Key: filePath,
      Body: file,
      SliceSize: 1024 * 1024 * 5, // 5MB分片
      onProgress: (progressData) => {
        console.log(`COS上传进度：${(progressData.percent * 100).toFixed(2)}%`)
      },
      onFileFinish: (err, data) => {
        if (err) {
          reject(new Error(`腾讯云COS上传失败：${err.message}`))
        } else if (data.statusCode === 200) {
          resolve(token.data.staticUrl + filePath)
        }
      }
    })
  })
}

/**
 * 生成文件路径（保留原有逻辑）
 */
export function uploadOss(fileName, prefix) {
  const suffix = fileName.substring(fileName.lastIndexOf('.'))
  return generateFilePath(prefix, suffix)
}

/**
 * 生成带日期的文件路径
 */
export function generateFilePath(prefix, suffix) {
  const now = new Date()
  const year = now.getFullYear()
  const month = (now.getMonth() + 1).toString().padStart(2, '0')
  const day = now.getDate().toString().padStart(2, '0')

  return `/${prefix || 'default'}/${year}/${month}/${day}/${now.getTime()}${generateRdStr()}${suffix}`
}

/**
 * 生成随机字符串
 */
export function generateRdStr() {
  const len = 15
  let text = ''
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  for (let i = 0; i < len; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length))
  }
  return text
}

// ===================== 适配OSS文件管理的核心方法 =====================
/**
 * 列出目录下的文件/目录（前端直查，支持阿里云/腾讯云）
 * @param dirPath 相对根路径的目录（空则查根目录）
 */
export async function listOssFiles(dirPath = '') {
  const stsToken = await getSTSToken()
  const type = stsToken.data.type
  const fullPath = ROOT_PREFIX + (dirPath ? dirPath : '')
  const normalizedPath = fullPath.endsWith('/') ? fullPath : `${fullPath}/`

  if (type === 'aliyun') {
    // 阿里云OSS列举文件
    const client = await initAliOssClient()
    const result = await client.list({
      prefix: normalizedPath,
      delimiter: '/',
      'max-keys': 100
    })
    return formatAliOssFileList(result, normalizedPath)
  } else if (type === 'tencent') {
    // 腾讯云COS列举文件
    const cos = initTencentCosClient()
    return new Promise((resolve, reject) => {
      cos.getBucket({
        Bucket: stsToken.data.bucket,
        Region: stsToken.data.region,
        Prefix: normalizedPath,
        Delimiter: '/'
      }, (err, data) => {
        if (err) reject(err)
        resolve(formatCosFileList(data, normalizedPath))
      })
    })
  } else {
    throw new Error(`不支持的存储类型：${type}`)
  }
}

/**
 * 创建目录（前端直创，支持阿里云/腾讯云）
 * @param dirPath 相对根路径的目录路径
 */
export async function createOssDir(dirPath) {
  const stsToken = await getSTSToken()
  const type = stsToken.data.type
  const fullPath = `${ROOT_PREFIX}${dirPath}/`

  if (type === 'aliyun') {
    const client = await initAliOssClient()
    await client.put(fullPath, new Blob([]), { headers: getHeaders() })
  } else if (type === 'tencent') {
    const cos = initTencentCosClient()
    return new Promise((resolve, reject) => {
      cos.putObject({
        Bucket: stsToken.data.bucket,
        Region: stsToken.data.region,
        Key: fullPath,
        Body: ''
      }, (err) => {
        if (err) reject(err)
        resolve(true)
      })
    })
  }
  return true
}

/**
 * 上传文件夹（前端直传，支持阿里云/腾讯云）
 * @param files 文件列表（含相对路径）
 * @param targetFolder 目标文件夹路径
 */
export async function uploadOssFolder(files, targetFolder) {
  const stsToken = await getSTSToken()
  const type = stsToken.data.type
  let successCount = 0

  for (const file of files) {
    // 拼接完整文件路径
    const filePath = `${ROOT_PREFIX}${targetFolder}/${file.relativePath}`
    if (type === 'aliyun') {
      await uploadOssObject(filePath, file.raw, '')
    } else if (type === 'tencent') {
      await uploadCosObject(filePath, file.raw, stsToken)
    }
    successCount++
  }
  return successCount
}

// ===================== 工具方法：格式化文件列表 =====================
/**
 * 格式化阿里云OSS文件列表
 */
function formatAliOssFileList(result, normalizedPath) {
  const fileList = []
  // 处理目录
  if (result.prefixes && result.prefixes.length) {
    result.prefixes.forEach(prefix => {
      const relativePath = prefix.replace(ROOT_PREFIX, '')
      fileList.push({
        fileName: relativePath.slice(0, -1),
        filePath: relativePath,
        isDir: true,
        fileSize: 0
      })
    })
  }
  // 处理文件
  if (result.objects && result.objects.length) {
    result.objects.forEach(obj => {
      if (obj.name === normalizedPath) return
      const relativePath = obj.name.replace(ROOT_PREFIX, '')
      fileList.push({
        fileName: relativePath.split('/').pop(),
        filePath: relativePath,
        isDir: false,
        fileSize: obj.size,
        fileUrl: `${obj.url}?${new Date().getTime()}` // 防缓存
      })
    })
  }
  return fileList
}

/**
 * 格式化腾讯云COS文件列表
 */
function formatCosFileList(data, normalizedPath) {
  const fileList = []
  // 处理目录
  if (data.CommonPrefixes && data.CommonPrefixes.length) {
    data.CommonPrefixes.forEach(prefix => {
      const relativePath = prefix.Prefix.replace(ROOT_PREFIX, '')
      fileList.push({
        fileName: relativePath.slice(0, -1),
        filePath: relativePath,
        isDir: true,
        fileSize: 0
      })
    })
  }
  // 处理文件
  if (data.Contents && data.Contents.length) {
    data.Contents.forEach(obj => {
      if (obj.Key === normalizedPath) return
      const relativePath = obj.Key.replace(ROOT_PREFIX, '')
      fileList.push({
        fileName: relativePath.split('/').pop(),
        filePath: relativePath,
        isDir: false,
        fileSize: obj.Size,
        fileUrl: `https://${data.Name}.cos.${data.Location}.myqcloud.com/${obj.Key}`
      })
    })
  }
  return fileList
}

export function listFiles(dirPath) {
  return request({
    url: '/system/oss/list',
    method: 'get',
    params: { dirPath: dirPath }
  })
}

export function createDir(dirPath) {
  return request({
    url: '/system/oss/createDir',
    method: 'post',
    params: { dirPath: dirPath }
  })
}

export function uploadFile(data) {
  return request({
    url: '/system/oss/uploadFile',
    method: 'post',
    data: data,
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

export function uploadFolder(data) {
  return request({
    url: '/system/oss/uploadFolder',
    method: 'post',
    data: data,
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}